/**
 * @file BrandWordmark.tsx
 * @brief Full Verblizr wordmark ("VERBLIZR" + tagline + icon) — automatically
 *        theme-aware.
 *
 * @details
 * Two source images sit under `/brand/`:
 * - `wordmark-dark.png`  — black text + red icon, designed for light surfaces.
 * - `wordmark-light.png` — white text + red icon, designed for dark surfaces.
 *
 * We render both inside the same `<picture>` element and use Tailwind's
 * `dark:` variant to swap visibility. This avoids any JS-driven flicker
 * during theme changes and keeps the right asset cached by the browser
 * for both modes.
 *
 * @dependencies
 * - WEBPANEL/public/brand/wordmark-dark.png  (light-theme asset)
 * - WEBPANEL/public/brand/wordmark-light.png (dark-theme asset)
 *
 * @references
 * - FRONTEND/src/assets/appfooter_black.png  (mapped to wordmark-dark)
 * - FRONTEND/src/assets/appfooter_white.png  (mapped to wordmark-light)
 *
 * @author DES
 * @date 26-05-2026
 */

import type { HTMLAttributes } from "react";
import { cn } from "@/shared/theme/cn";

interface BrandWordmarkProps extends HTMLAttributes<HTMLDivElement> {
  /** Pixel height for the wordmark (default 32). Width auto-scales. */
  height?: number;
}

export function BrandWordmark({
  height = 32,
  className,
  ...rest
}: BrandWordmarkProps) {
  // ----------------------------------------------------------------------
  // We render two <img> tags and toggle visibility with `dark:` variants.
  // The CSS-only swap means no flash-of-wrong-asset when theme changes.
  // ----------------------------------------------------------------------
  return (
    <div
      className={cn("inline-flex items-center", className)}
      style={{ height }}
      {...rest}
    >
      <img
        src="/brand/wordmark-dark.png"
        alt="Verblizr — Communicate Together"
        height={height}
        draggable={false}
        className="block h-full w-auto select-none object-contain dark:hidden"
      />
      <img
        src="/brand/wordmark-light.png"
        alt=""
        aria-hidden
        height={height}
        draggable={false}
        className="hidden h-full w-auto select-none object-contain dark:block"
      />
    </div>
  );
}
